import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
// import { DeviceColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { MySiteFilter } from "../Components/FilterComponent";
import { MysiteColumns } from "./Columns";
import { SiteBlockedModal } from "../Components/SiteBlockedModal";
import { getUserRole } from "../../../global/hooks/getUserRole";

const initialFilterState = {
  DeviceNo: "",
  DeviceName: "",
  Description: "",
  SiteType: null,
  OemNo: null,
  DealerNo: null,
  CustomerNo: null,
  Mac: "",
  Version: "",
  Address: "",
  Online: null,
  RMC: null,
};

export const MySite = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [modal, setModal] = useState(null);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "DeviceNo",
    order: "desc",
    NodeType: "1",
  }); 

  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  }; //
  const [pagination, setPagination] = useState(initialPagination);
  const [filter, setFilter] = useState(initialFilterState);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filterNoValueText, setFilterNoValueText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [columnsValues, setColumnsValues] = useState([]);
  const [resetTrigger, setResetTrigger] = useState(false);

  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      label: "Site",
      href: "/site",
    },
  ];

  // to get table data from API server
  const query = new URLSearchParams(params);
  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();

  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/sites/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/sites/`;
  const { data, loading, error, refetch } = useGet(url);

  let deleteUrl;
  deleteUrl = `${process.env.REACT_APP_API_URL}/api/sites/bulk-delete/`;

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  }; 

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // navigate(`/oemlist/${newSelectedRowKeys}`);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record) => {
    return {
      onClick: (event) => {
        navigate(`/site/info/${record?.DeviceNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";
    if (isEmpty) {
      setFilterNoValueText("No value exists!");
      handleReset();
    } else {
      const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //

      const tempParams = {
        // ...modParams,
        ...filterDict,
        limit: pagination.pageSize,
        sort_by: modParams.sort_by,
        order: modParams.order,
        page: 1,
        NodeType: "1",
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(tempParams);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "DeviceNo", // Make sure to specify sorting if necessary
      order: "desc",
      NodeType: "1",
    };

    // Update the parameters in the state
    setParams(params);
    refetch();
    setSelectedRowKeys([]);
    setResetTrigger(!resetTrigger);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteMultiple({ ids: selectedRowKeys });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong"
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.DeviceNo,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <MySiteFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setShowModal={setShowModal}
          selectedRowKeys={selectedRowKeys}
          setFilter={setFilter}
          data={data}
          setColumnsValues={setColumnsValues}
        />
        <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          onDeleteConfirm={onDeleteConfirm}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <div className="mt-4">
          <GlobalTable
            columns={MysiteColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              setModal,
              columnsValues
            )}
            dataSource={listData}
            rowSelection={
              [1, 2, 3, 7, 8, 9].includes(getUserRole()) ? rowSelection : null
            }
            pagination={paginationConfig}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
            resize
            resetTrigger={resetTrigger}
          />
        </div>
      </div>
      <SiteBlockedModal
        isModalOpen={modal !== null}
        setModal={setModal}
        modal={modal}
      />
    </div>
  );
};
